import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Apple,
  ArrowRight,
  ArrowRightLeft,
  BadgeCheck,
  BookOpen,
  CircleAlert,
  ClipboardList,
  FileText,
  Filter,
  House,
  Leaf,
  ListChecks,
  NotebookPen,
  Package,
  Recycle,
  Scale,
  Search,
  ShieldCheck,
  SprayCan,
  Wind,
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';

const heroInfoCards = [
  {
    id: 'ingredients',
    label: 'összetevők',
    tooltip: 'Mire érdemes figyelni a címkéken a mindennapi döntéseknél.',
    positionClass: 'top-4 right-4 md:top-8 md:right-8',
  },
  {
    id: 'packaging',
    label: 'csomagolás',
    tooltip: 'Anyaghasználat, újrahasznosíthatóság és praktikus szempontok.',
    positionClass: 'top-20 left-3 md:top-28 md:left-6',
  },
  {
    id: 'research',
    label: 'kutatások',
    tooltip: 'Folyamatosan követett szakmai források és friss eredmények.',
    positionClass: 'bottom-20 right-8 md:bottom-24 md:right-16',
  },
  {
    id: 'alternatives',
    label: 'jobb alternatívák',
    tooltip: 'Konkrét, gyakorlatias alternatívák egy tudatosabb életmódhoz.',
    positionClass: 'bottom-4 left-4 md:bottom-8 md:left-10',
  },
];

const noiseLabels = [
  'félinformáció',
  'túlzó ígéretek',
  'ellentmondó állítások',
  'ijesztgetés',
  'vélemény',
  'hangzatos címek',
];

const clearDecisionCards = [
  'hiteles forrás',
  'kutatások',
  'közérthető összefoglaló',
  'gyakorlati tanács',
  'jobb alternatíva',
];

const missionCards = [
  {
    id: 'source',
    title: 'Hiteles forrásokat követünk',
    text: 'Folyamatosan figyeljük az új kutatásokat és szakmai forrásokat.',
    details:
      'Elkötelezettek vagyunk amellett, hogy nyomon kövessük az új eredményeket, és csak olyan információt emeljünk ki, ami valódi kapaszkodót ad.',
    icon: Search,
  },
  {
    id: 'summary',
    title: 'Közérthetően összefoglaljuk',
    text: 'A bonyolult információkat gyakorlati, könnyen érthető formában adjuk át.',
    details:
      'Nem félelmet keltő, hanem tisztázó megközelítést használunk, hogy valóban alkalmazható legyen a tudás a hétköznapokban.',
    icon: NotebookPen,
  },
  {
    id: 'alternatives',
    title: 'Jobb alternatívákat ajánlunk',
    text: 'Olyan termékeket válogatunk, amelyek támogatják az egészségtudatosabb életmódot.',
    details:
      'A célunk, hogy a tájékozódás után rögtön találj gyakorlati opciókat is, ne csak elméleti információt.',
    icon: BadgeCheck,
  },
];

const topicModules = [
  {
    id: 'food',
    title: 'Élelmiszer-összetevők',
    description:
      'Közérthető módon segítünk értelmezni az összetevőlistákat, hogy tudd, mire érdemes figyelned.',
    panelText:
      'Címkék, összetevőlisták, mennyiségek és hétköznapi értelmezés. Később ide cikkek, elemzések és kapcsolódó ajánlók kerülhetnek.',
    icon: FileText,
    positionClass: 'top-3 left-1/2 -translate-x-1/2',
  },
  {
    id: 'packaging',
    title: 'Csomagolóanyagok',
    description:
      'Megmutatjuk, milyen csomagolási megoldások lehetnek praktikusabbak és fenntarthatóbbak.',
    panelText:
      'Anyagismeret, újrahasznosítási szempontok, tárolás és fogyasztói döntések. Ide kerülhetnek útmutatók és termékpéldák.',
    icon: Recycle,
    positionClass: 'top-1/2 right-0 -translate-y-1/2',
  },
  {
    id: 'household',
    title: 'Háztartási termékek',
    description:
      'A mindennapi tisztítás és otthoni használat terén segítünk jobb alternatívákat találni.',
    panelText:
      'Összetevőszemlélet, használati gyakoriság és célzott választás. Itt kaphatnak helyet gyakorlati tisztítási ajánlók.',
    icon: SprayCan,
    positionClass: 'bottom-2 left-1/4 -translate-x-1/2',
  },
  {
    id: 'environment',
    title: 'Környezeti terhelések',
    description:
      'Levegő, víz és otthoni környezet: hogyan érdemes józanul értelmezni a kockázatokat.',
    panelText:
      'Fókuszban a mindennapi kitettség és a valós gyakorlati lépések. Ide kerülhetnek cikkek, ellenőrzőlisták és útmutatók.',
    icon: Wind,
    positionClass: 'bottom-2 right-1/4 translate-x-1/2',
  },
];

const opinionVsFactItems = [
  {
    id: 'claim-1',
    opinion: 'Ezt mindenki veszélyesnek mondja.',
    factQuestions: [
      'Mit mondanak a hiteles források?',
      'Milyen mennyiségről van szó?',
      'Van-e gyakorlati kockázat a mindennapokban?',
    ],
    summary:
      'A szenzáció helyett a kontextus számít: forrás, dózis és valós használati környezet alapján érdemes dönteni.',
  },
  {
    id: 'claim-2',
    opinion: 'Egy videóban ezt láttam.',
    factQuestions: [
      'Milyen kutatásra hivatkozik a tartalom?',
      'Reprodukálható-e az állítás?',
      'Mire vonatkozik pontosan az eredmény?',
    ],
    summary:
      'Egyetlen tartalom nem bizonyíték. Több forrást, módszert és következtetést együtt kell értelmezni.',
  },
  {
    id: 'claim-3',
    opinion: 'Valaki szerint teljesen ártalmatlan.',
    factQuestions: [
      'Milyen feltételek mellett igaz ez az állítás?',
      'Kiknek lehet eltérő kockázatuk?',
      'Mi a biztonságos, gyakorlatias döntés?',
    ],
    summary:
      'A szélsőségek helyett a kiegyensúlyozott, forrásalapú megközelítés segít fenntartható döntéseket hozni.',
  },
];

const journeySteps = [
  {
    id: 'step-1',
    title: 'Megérted, mire érdemes figyelni',
    description: 'Átlátható útmutatót kapsz a fontos összetevőkről és szempontokról.',
    icon: BookOpen,
  },
  {
    id: 'step-2',
    title: 'Különválasztod a tényeket a túlzásoktól',
    description: 'A források és állítások közötti különbségeket közérthetően mutatjuk meg.',
    icon: Scale,
  },
  {
    id: 'step-3',
    title: 'Jobb alternatívákat találsz',
    description: 'A tájékozódást azonnal át tudod fordítani konkrét termékválasztásra.',
    icon: Package,
  },
  {
    id: 'step-4',
    title: 'Apró döntésekkel változtatsz a mindennapokon',
    description: 'Fokozatos, hosszú távon is tartható, gyakorlatias változtatásokban gondolkodunk.',
    icon: Leaf,
  },
];

const trustKeywords = [
  {
    label: 'közérthető',
    tip: 'A szakmai információkat hétköznapi nyelven, világos példákkal adjuk át.',
  },
  {
    label: 'gyakorlatias',
    tip: 'Minden tartalom célja, hogy valódi döntési segítséget adjon a napi rutinban.',
  },
  {
    label: 'forrásalapú',
    tip: 'A fontos állításokat hiteles forrásokra és követhető logikára építjük.',
  },
  {
    label: 'kiegyensúlyozott',
    tip: 'Kerüljük a szélsőségeket: nem ijesztgetünk, de nem is bagatellizálunk.',
  },
  {
    label: 'nem félelemkeltő',
    tip: 'A cél a nyugodt, informált és fenntartható döntéshozatal támogatása.',
  },
];

export default function About() {
  const [expandedMission, setExpandedMission] = useState(missionCards[0].id);
  const [activeTopic, setActiveTopic] = useState(topicModules[0].id);
  const [activeOpinion, setActiveOpinion] = useState(opinionVsFactItems[0].id);
  const [activeJourneyStep, setActiveJourneyStep] = useState(0);
  const journeyRefs = useRef([]);

  const selectedTopic = useMemo(
    () => topicModules.find((topic) => topic.id === activeTopic) || topicModules[0],
    [activeTopic],
  );
  const selectedOpinion = useMemo(
    () => opinionVsFactItems.find((item) => item.id === activeOpinion) || opinionVsFactItems[0],
    [activeOpinion],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const nextIndex = Number(entry.target.getAttribute('data-step-index'));
          if (Number.isFinite(nextIndex)) {
            setActiveJourneyStep(nextIndex);
          }
        });
      },
      {
        threshold: 0.55,
        rootMargin: '-10% 0px -35% 0px',
      },
    );

    const targets = journeyRefs.current.filter(Boolean);
    targets.forEach((target) => observer.observe(target));

    return () => observer.disconnect();
  }, []);

  return (
    <Layout>
      <div className="section-padding bg-background">
        <div className="container-zinga space-y-16 md:space-y-24">
          <section className="rounded-3xl border border-border/70 bg-card/70 p-8 md:p-12 shadow-soft backdrop-blur-sm">
            <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] items-center">
              <div className="space-y-6">
                <span className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-2 text-xs font-semibold tracking-wide text-foreground/80">
                  <ShieldCheck className="h-4 w-4" />
                  Rólunk
                </span>
                <h1 className="font-display text-4xl md:text-5xl leading-tight font-bold">
                  Egészségesebb döntések a mindennapokra
                </h1>
                <p className="text-lg text-foreground/80 leading-relaxed">
                  Gondosan válogatott termékek és közérthető, hiteles információk egy tudatosabb életmódhoz.
                </p>
                <p className="text-foreground/80 leading-relaxed">
                  Olyan alternatívákat gyűjtünk össze, amelyek segítenek tudatosabban választani abban, mi kerül a
                  szervezetedbe és mi vesz körül a hétköznapokban. Mi egy olyan weboldalt építünk, ahol egyszerre kapsz
                  minőségi termékeket és megbízható, gyakorlatias tájékoztatást.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/shop" className="btn-primary">
                    Fedezd fel a termékeket
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <a href="#temakorok" className="btn-secondary">
                    Tudj meg többet a káros anyagokról
                  </a>
                </div>
              </div>

              <div className="relative rounded-3xl border border-border/70 bg-background/80 p-4 md:p-6 min-h-[360px] overflow-hidden">
                <div className="absolute inset-4 rounded-2xl border border-border/70 bg-secondary/40" />

                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-3">
                  <span className="flex h-20 w-20 items-center justify-center rounded-2xl bg-card shadow-soft border border-border/70">
                    <House className="h-9 w-9 text-primary" />
                  </span>
                  <p className="text-xs font-semibold tracking-wide text-foreground/70 uppercase">Tudatos mindennapok</p>
                </div>

                <div className="absolute left-[22%] top-[28%] flex h-14 w-14 items-center justify-center rounded-xl border border-border/70 bg-card shadow-soft about-float-slow">
                  <Apple className="h-7 w-7 text-primary" />
                </div>
                <div className="absolute right-[18%] top-[30%] flex h-14 w-14 items-center justify-center rounded-xl border border-border/70 bg-card shadow-soft about-float-medium">
                  <SprayCan className="h-7 w-7 text-primary" />
                </div>
                <div className="absolute left-[30%] bottom-[22%] flex h-14 w-14 items-center justify-center rounded-xl border border-border/70 bg-card shadow-soft about-float-medium">
                  <Package className="h-7 w-7 text-primary" />
                </div>
                <div className="absolute right-[28%] bottom-[22%] flex h-14 w-14 items-center justify-center rounded-xl border border-border/70 bg-card shadow-soft about-float-slow">
                  <Leaf className="h-7 w-7 text-primary" />
                </div>

                <span className="absolute left-[36%] top-[43%] h-px w-[20%] bg-border/80 about-pulse-line" />
                <span className="absolute right-[35%] top-[44%] h-px w-[20%] bg-border/80 about-pulse-line" />
                <span className="absolute left-[42%] bottom-[37%] h-px w-[15%] bg-border/80 about-pulse-line" />
                <span className="absolute right-[41%] bottom-[37%] h-px w-[15%] bg-border/80 about-pulse-line" />

                {heroInfoCards.map((card) => (
                  <div key={card.id} className={`absolute ${card.positionClass} group`}>
                    <div className="rounded-full border border-border/70 bg-card px-3 py-1.5 text-xs font-semibold shadow-soft transition-transform duration-200 group-hover:-translate-y-1">
                      {card.label}
                    </div>
                    <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 w-52 -translate-x-1/2 rounded-xl border border-border/70 bg-card p-3 text-xs text-muted-foreground shadow-soft opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      {card.tooltip}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="space-y-8">
            <div className="mx-auto max-w-4xl text-center space-y-4">
              <h2 className="font-display text-3xl md:text-4xl font-bold">
                A zajban nehéz megtalálni a megbízható információt
              </h2>
              <p className="text-foreground/80 leading-relaxed">
                Szenzációhajhász tartalmak, félinformációk és egymásnak ellentmondó állítások között egyre nehezebb
                eldönteni, mi igaz és mi túlzás. A közösségi média zajában ma már sokszor nehéz tisztán látni a
                szennyezéssel és káros anyagokkal kapcsolatos állításokat.
              </p>
            </div>

            <div className="relative grid gap-6 lg:grid-cols-[1fr_auto_1fr] items-stretch">
              <div className="rounded-3xl border border-border/70 bg-secondary/40 p-6 md:p-7">
                <p className="font-display text-xl font-semibold mb-4">Online zaj</p>
                <div className="flex flex-wrap gap-3">
                  {noiseLabels.map((label, index) => (
                    <span
                      key={label}
                      className={`rounded-full border border-border/70 bg-card px-3 py-1.5 text-sm text-foreground/70 about-float-slow ${
                        index % 2 ? 'about-float-delay-1' : ''
                      }`}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="hidden lg:flex flex-col items-center justify-center gap-4 px-2">
                <span className="h-24 w-px bg-border/80 about-pulse-line" />
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-border/70 bg-card shadow-soft">
                  <ArrowRightLeft className="h-5 w-5 text-primary" />
                </span>
                <span className="h-24 w-px bg-border/80 about-pulse-line" />
              </div>

              <div className="rounded-3xl border border-border/70 bg-card p-6 md:p-7">
                <p className="font-display text-xl font-semibold mb-4">Tisztább döntések</p>
                <div className="grid gap-3">
                  {clearDecisionCards.map((card) => (
                    <div
                      key={card}
                      className="rounded-xl border border-border/70 bg-background px-4 py-3 text-sm font-medium transition-transform duration-200 hover:-translate-y-0.5"
                    >
                      {card}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <div className="max-w-3xl">
              <h2 className="font-display text-3xl md:text-4xl font-bold">Mi ebben szeretnénk kapaszkodót adni</h2>
            </div>
            <div className="grid gap-5 lg:grid-cols-3">
              {missionCards.map((card) => {
                const Icon = card.icon;
                const isExpanded = expandedMission === card.id;
                return (
                  <article
                    key={card.id}
                    className={`rounded-2xl border bg-card p-6 shadow-soft transition-transform duration-200 hover:-translate-y-1 ${
                      isExpanded ? 'border-primary/40' : 'border-border/70'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-secondary">
                        <Icon className="h-6 w-6 text-primary" />
                      </span>
                      <button
                        type="button"
                        onClick={() => setExpandedMission(isExpanded ? '' : card.id)}
                        className="text-xs font-semibold text-foreground/70 hover:text-foreground"
                      >
                        Tudj meg többet
                      </button>
                    </div>
                    <h3 className="font-display text-xl font-semibold mt-5">{card.title}</h3>
                    <p className="mt-3 text-sm text-foreground/80 leading-relaxed">{card.text}</p>
                    {isExpanded && (
                      <div className="mt-4 rounded-xl border border-border/70 bg-background px-4 py-3 text-sm text-muted-foreground leading-relaxed animate-fade-in">
                        {card.details}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>

          <section id="temakorok" className="space-y-8">
            <div className="max-w-3xl">
              <h2 className="font-display text-3xl md:text-4xl font-bold">Miben segítünk eligazodni?</h2>
            </div>

            <div className="rounded-3xl border border-border/70 bg-card p-6 md:p-8">
              <div className="grid gap-8 lg:grid-cols-[1fr_0.95fr] items-center">
                <div className="relative mx-auto h-[320px] w-[320px] sm:h-[360px] sm:w-[360px]">
                  <div className="absolute inset-[28%] rounded-full border border-border/70 bg-secondary/60 flex items-center justify-center text-center px-4">
                    <p className="font-display text-lg sm:text-xl font-semibold">Tudatosabb mindennapok</p>
                  </div>
                  {topicModules.map((topic) => {
                    const Icon = topic.icon;
                    const isActive = activeTopic === topic.id;
                    return (
                      <button
                        key={topic.id}
                        type="button"
                        onClick={() => setActiveTopic(topic.id)}
                        className={`absolute ${topic.positionClass} -translate-y-1/2 sm:translate-y-0 rounded-2xl border px-3 py-2 text-left transition-all duration-200 ${
                          isActive
                            ? 'border-primary/40 bg-secondary shadow-soft scale-[1.04]'
                            : 'border-border/70 bg-background hover:-translate-y-0.5'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-primary" />
                          <span className="text-xs sm:text-sm font-semibold">{topic.title}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="rounded-2xl border border-border/70 bg-background p-5 md:p-6 space-y-4 animate-fade-in">
                  <div className="flex items-center gap-3">
                    <selectedTopic.icon className="h-5 w-5 text-primary" />
                    <h3 className="font-display text-2xl font-semibold">{selectedTopic.title}</h3>
                  </div>
                  <p className="text-foreground/80 leading-relaxed">{selectedTopic.description}</p>
                  <div className="rounded-xl border border-border/70 bg-card p-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">{selectedTopic.panelText}</p>
                    <div className="mt-4 grid gap-2 text-xs text-foreground/70">
                      <span className="inline-flex items-center gap-2">
                        <ListChecks className="h-4 w-4 text-primary" />
                        Kapcsolódó cikkek helye
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <ClipboardList className="h-4 w-4 text-primary" />
                        Tudástár linkek helye
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <Package className="h-4 w-4 text-primary" />
                        Termékajánlók helye
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="facts" className="space-y-8">
            <div className="max-w-3xl">
              <h2 className="font-display text-3xl md:text-4xl font-bold">Tények vs. vélemények</h2>
              <p className="mt-3 text-foreground/80 leading-relaxed">
                Segítünk különválasztani a tényeket a véleményektől, és forrásalapon végiggondolni az állításokat.
              </p>
            </div>

            <div className="rounded-3xl border border-border/70 bg-card p-6 md:p-8 grid gap-6 lg:grid-cols-[1fr_auto_1fr]">
              <div className="space-y-3">
                <p className="font-display text-xl font-semibold">Vélemény / zaj</p>
                {opinionVsFactItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveOpinion(item.id)}
                    className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
                      activeOpinion === item.id
                        ? 'border-primary/40 bg-secondary'
                        : 'border-border/70 bg-background hover:bg-secondary/40'
                    }`}
                  >
                    <span className="inline-flex items-start gap-2 text-sm">
                      <CircleAlert className="mt-0.5 h-4 w-4 text-primary" />
                      {item.opinion}
                    </span>
                  </button>
                ))}
              </div>

              <div className="hidden lg:flex items-center justify-center">
                <div className="flex flex-col items-center gap-4 rounded-2xl border border-border/70 bg-background px-5 py-6">
                  <Filter className="h-6 w-6 text-primary" />
                  <span className="h-16 w-px bg-border/80 about-pulse-line" />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Tisztázás</span>
                  <span className="h-16 w-px bg-border/80 about-pulse-line" />
                </div>
              </div>

              <div className="space-y-4 rounded-2xl border border-primary/30 bg-secondary/40 p-5">
                <p className="font-display text-xl font-semibold">Tény / forrás</p>
                <div className="space-y-2">
                  {selectedOpinion.factQuestions.map((question) => (
                    <div key={question} className="rounded-lg border border-border/70 bg-card px-3 py-2 text-sm">
                      {question}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed">{selectedOpinion.summary}</p>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <div className="max-w-3xl">
              <h2 className="font-display text-3xl md:text-4xl font-bold">Információ és jobb alternatívák egy helyen</h2>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1fr_auto_1fr] items-stretch">
              <article className="rounded-2xl border border-border/70 bg-card p-6 shadow-soft transition-transform duration-200 hover:-translate-y-1">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-secondary">
                  <BookOpen className="h-6 w-6 text-primary" />
                </span>
                <h3 className="font-display text-2xl font-semibold mt-5">
                  Mit érdemes tudni a háztartási tisztítószerekről?
                </h3>
                <p className="mt-3 text-sm text-foreground/80 leading-relaxed">
                  Közérthető összefoglalók a legfontosabb szempontokról, hogy tudd, mire figyelj vásárláskor és
                  használatkor.
                </p>
                <a href="#facts" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-foreground/80 hover:text-foreground">
                  Olvasd el
                  <ArrowRight className="h-4 w-4" />
                </a>
              </article>

              <div className="hidden lg:flex items-center justify-center">
                <div className="rounded-full border border-border/70 bg-background px-4 py-2 text-xs font-semibold text-muted-foreground about-float-medium">
                  ajánlott alternatíva
                </div>
              </div>

              <article className="rounded-2xl border border-border/70 bg-secondary/40 p-6 shadow-soft transition-transform duration-200 hover:-translate-y-1">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-card">
                  <Package className="h-6 w-6 text-primary" />
                </span>
                <h3 className="font-display text-2xl font-semibold mt-5">
                  Válogatott, egészségtudatosabb tisztítási alternatívák
                </h3>
                <p className="mt-3 text-sm text-foreground/80 leading-relaxed">
                  Olyan termékeket gyűjtünk, amelyek támogatják a tudatosabb, nyugodtabb és praktikusabb hétköznapokat.
                </p>
                <Link to="/shop?category=household" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-foreground/80 hover:text-foreground">
                  Termékek megtekintése
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            </div>
          </section>

          <section className="space-y-8">
            <div className="max-w-3xl">
              <h2 className="font-display text-3xl md:text-4xl font-bold">Tudatosabb életmód, lépésről lépésre</h2>
            </div>

            <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="space-y-5">
                {journeySteps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = activeJourneyStep === index;
                  return (
                    <article
                      key={step.id}
                      data-step-index={index}
                      ref={(node) => {
                        journeyRefs.current[index] = node;
                      }}
                      className={`rounded-2xl border bg-card p-5 transition-all duration-200 ${
                        isActive ? 'border-primary/40 shadow-soft' : 'border-border/70'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-secondary shrink-0">
                          <Icon className="h-5 w-5 text-primary" />
                        </span>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Lépés {index + 1}</p>
                          <h3 className="font-display text-xl font-semibold">{step.title}</h3>
                          <p className="mt-2 text-sm text-foreground/80 leading-relaxed">{step.description}</p>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              <aside className="lg:sticky lg:top-24 self-start rounded-2xl border border-border/70 bg-secondary/40 p-6">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Aktuális fókusz</p>
                <h3 className="font-display text-2xl font-semibold mt-2">{journeySteps[activeJourneyStep].title}</h3>
                <p className="mt-3 text-sm text-foreground/80 leading-relaxed">
                  {journeySteps[activeJourneyStep].description}
                </p>
                <div className="mt-5 grid grid-cols-4 gap-2">
                  {journeySteps.map((step, index) => (
                    <span
                      key={step.id}
                      className={`h-1.5 rounded-full ${index <= activeJourneyStep ? 'bg-primary' : 'bg-border'}`}
                    />
                  ))}
                </div>
              </aside>
            </div>
          </section>

          <section className="relative overflow-hidden rounded-3xl border border-border/70 bg-card p-8 md:p-12">
            <div className="absolute inset-0 about-surface-pattern opacity-70" aria-hidden="true" />
            <div className="relative z-10 grid gap-8 lg:grid-cols-[1.1fr_0.9fr] items-start">
              <div className="space-y-5">
                <h2 className="font-display text-3xl md:text-4xl font-bold">
                  Felesleges ijesztgetés és túlzó ígéretek nélkül
                </h2>
                <p className="text-foreground/80 leading-relaxed">
                  Hiszünk abban, hogy a jó döntésekhez jó információ kell. Ezért közérthető, gyakorlati és hiteles
                  tájékoztatást adunk, miközben olyan termékeket válogatunk, amelyekkel könnyebb egészségtudatosabb
                  életmódot kialakítani.
                </p>
                <p className="text-foreground/80 leading-relaxed">
                  Tájékoztatunk a mindennapokban előforduló káros anyagokról legyen szó élelmiszer-összetevőkről,
                  csomagolóanyagokról, háztartási termékekről vagy környezeti terhelésekről, és segítünk
                  különválasztani a tényeket a véleményektől. Lépésről lépésre haladunk, nem félelemkeltéssel.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 content-start">
                {trustKeywords.map((keyword, index) => (
                  <div
                    key={keyword.label}
                    className={`relative group rounded-full border border-border/70 bg-secondary px-4 py-2 text-sm font-semibold about-float-medium ${
                      index % 2 ? 'about-float-delay-1' : ''
                    }`}
                    style={{ animationDelay: `${index * 0.12}s` }}
                  >
                    {keyword.label}
                    <div className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-52 -translate-x-1/2 rounded-xl border border-border/70 bg-card p-3 text-xs font-medium text-muted-foreground shadow-soft opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      {keyword.tip}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="relative overflow-hidden rounded-3xl border border-border/70 bg-secondary/40 p-8 md:p-12">
            <div className="absolute -top-12 -left-12 h-36 w-36 rounded-full bg-card/60 blur-xl" aria-hidden="true" />
            <div className="absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-card/50 blur-xl" aria-hidden="true" />
            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-bold">
                Kezdd el tudatosabban alakítani a mindennapjaidat
              </h2>
              <p className="mt-4 text-foreground/80 leading-relaxed">
                Fedezd fel a válogatott termékeket és a közérthető tudástárat.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/shop" className="btn-primary">
                  Termékek felfedezése
                </Link>
                <a href="#temakorok" className="btn-secondary">
                  Tudástár megnyitása
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}
